// Catálogo Maestro de más de 160 Tipografías Románticas, Artísticas, Pixel y Cuadradas
// Categorías: Píxel, Cuadrada, Manuscrita, Cursiva, Elegante, Poética, Moderna, Vintage, Decorativa

export type FontCategory =
  | 'all'
  | 'pixel'
  | 'cuadrada'
  | 'manuscrita'
  | 'cursiva'
  | 'elegante'
  | 'poetica'
  | 'moderna'
  | 'vintage'
  | 'decorativa';

export interface RomanticFontItem {
  id: string;
  name: string;
  googleFamily: string;
  googleFontQuery?: string;
  category: Exclude<FontCategory, 'all'>;
  categoryLabel: string;
  style: 'normal' | 'italic';
  weight: number | string;
  cssFamily: string;
  baseSize: number;
  baseLineHeight: number;
  inkColor?: string;
  sampleText?: string;
}

export const FONT_CATEGORY_TABS: { id: FontCategory; label: string; count?: number }[] = [
  { id: 'all', label: 'Todas' },
  { id: 'pixel', label: 'Píxel / 8-Bit' },
  { id: 'cuadrada', label: 'Cuadrada' },
  { id: 'manuscrita', label: 'Manuscrita' },
  { id: 'cursiva', label: 'Cursiva' },
  { id: 'elegante', label: 'Elegante' },
  { id: 'poetica', label: 'Poética' },
  { id: 'moderna', label: 'Moderna' },
  { id: 'vintage', label: 'Vintage' },
  { id: 'decorativa', label: 'Decorativa' },
];

export const ALL_ROMANTIC_FONTS: RomanticFontItem[] = [
  // ========================================================
  // 1. PÍXEL / 8-BIT / ARCADE (Retro gaming, amor pixel art, chiptune)
  // ========================================================
  {
    id: 'press-start',
    name: 'Press Start 2P',
    googleFamily: 'Press Start 2P',
    googleFontQuery: 'Press+Start+2P',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Press Start 2P', monospace",
    baseSize: 52,
    baseLineHeight: 88,
    sampleText: '100% AMOR NIVEL 99',
  },
  {
    id: 'vt323',
    name: 'VT323',
    googleFamily: 'VT323',
    googleFontQuery: 'VT323',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'VT323', monospace",
    baseSize: 84,
    baseLineHeight: 110,
    sampleText: 'CONEXIÓN: AMOR ETERNO',
  },
  {
    id: 'silkscreen',
    name: 'Silkscreen',
    googleFamily: 'Silkscreen',
    googleFontQuery: 'Silkscreen:wght@400;700',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 700,
    cssFamily: "'Silkscreen', monospace",
    baseSize: 64,
    baseLineHeight: 96,
    sampleText: 'Eres mi jugador dos',
  },
  {
    id: 'pixelify',
    name: 'Pixelify Sans',
    googleFamily: 'Pixelify Sans',
    googleFontQuery: 'Pixelify+Sans:wght@400;600;700',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 600,
    cssFamily: "'Pixelify Sans', sans-serif",
    baseSize: 72,
    baseLineHeight: 104,
    sampleText: 'Te elijo hoy y siempre',
  },
  {
    id: 'dot-gothic',
    name: 'DotGothic16',
    googleFamily: 'DotGothic16',
    googleFontQuery: 'DotGothic16',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'DotGothic16', monospace",
    baseSize: 68,
    baseLineHeight: 100,
    sampleText: 'Nuestra historia en bits',
  },
  {
    id: 'jersey-10',
    name: 'Jersey 10',
    googleFamily: 'Jersey 10',
    googleFontQuery: 'Jersey+10',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Jersey 10', sans-serif",
    baseSize: 80,
    baseLineHeight: 112,
    sampleText: 'Siempre a tu lado',
  },
  {
    id: 'jersey-15',
    name: 'Jersey 15',
    googleFamily: 'Jersey 15',
    googleFontQuery: 'Jersey+15',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Jersey 15', sans-serif",
    baseSize: 80,
    baseLineHeight: 112,
    sampleText: 'Amor de otro nivel',
  },
  {
    id: 'jersey-25',
    name: 'Jersey 25',
    googleFamily: 'Jersey 25',
    googleFontQuery: 'Jersey+25',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Jersey 25', sans-serif",
    baseSize: 82,
    baseLineHeight: 114,
    sampleText: 'Juntos sin final',
  },
  {
    id: 'tiny5',
    name: 'Tiny5',
    googleFamily: 'Tiny5',
    googleFontQuery: 'Tiny5',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Tiny5', monospace",
    baseSize: 60,
    baseLineHeight: 92,
    sampleText: 'Cada latido cuenta',
  },
  {
    id: 'rubik-pixels',
    name: 'Rubik Pixels',
    googleFamily: 'Rubik Pixels',
    googleFontQuery: 'Rubik+Pixels',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Rubik Pixels', system-ui",
    baseSize: 72,
    baseLineHeight: 106,
    sampleText: 'Pixel por pixel te amo',
  },
  {
    id: 'rubik-glitch',
    name: 'Rubik Glitch',
    googleFamily: 'Rubik Glitch',
    googleFontQuery: 'Rubik+Glitch',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Rubik Glitch', system-ui",
    baseSize: 70,
    baseLineHeight: 104,
    sampleText: 'Amor a prueba de todo',
  },
  {
    id: 'jacquarda-bastarda',
    name: 'Jacquarda Bastarda 9',
    googleFamily: 'Jacquarda Bastarda 9',
    googleFontQuery: 'Jacquarda+Bastarda+9',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Jacquarda Bastarda 9', serif",
    baseSize: 76,
    baseLineHeight: 108,
    sampleText: 'Juramento eterno',
  },
  {
    id: 'jacquard-12',
    name: 'Jacquard 12',
    googleFamily: 'Jacquard 12',
    googleFontQuery: 'Jacquard+12',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Jacquard 12', serif",
    baseSize: 76,
    baseLineHeight: 108,
    sampleText: 'Amor tejido en el alma',
  },
  {
    id: 'jacquard-24',
    name: 'Jacquard 24',
    googleFamily: 'Jacquard 24',
    googleFontQuery: 'Jacquard+24',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Jacquard 24', serif",
    baseSize: 78,
    baseLineHeight: 110,
    sampleText: 'Historia que perdura',
  },
  {
    id: 'honk',
    name: 'Honk',
    googleFamily: 'Honk',
    googleFontQuery: 'Honk',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Honk', system-ui",
    baseSize: 72,
    baseLineHeight: 106,
    sampleText: 'Amor a todo color',
  },
  {
    id: 'bungee-shade',
    name: 'Bungee Shade',
    googleFamily: 'Bungee Shade',
    googleFontQuery: 'Bungee+Shade',
    category: 'pixel',
    categoryLabel: 'Píxel',
    style: 'normal',
    weight: 400,
    cssFamily: "'Bungee Shade', cursive, sans-serif",
    baseSize: 66,
    baseLineHeight: 100,
    sampleText: 'Late con fuerza',
  },

  // ========================================================
  // 2. CUADRADA / BLOQUE / GEOMÉTRICA (Estructura cuadrada, ángulos rectos)
  // ========================================================
  {
    id: 'orbitron',
    name: 'Orbitron',
    googleFamily: 'Orbitron',
    googleFontQuery: 'Orbitron:wght@400;600;700;900',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 700,
    cssFamily: "'Orbitron', sans-serif",
    baseSize: 72,
    baseLineHeight: 106,
    sampleText: 'DESTINO ALINEADO',
  },
  {
    id: 'squada-one',
    name: 'Squada One',
    googleFamily: 'Squada One',
    googleFontQuery: 'Squada+One',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Squada One', cursive, sans-serif",
    baseSize: 84,
    baseLineHeight: 114,
    sampleText: 'MI UNIVERSO ERES TÚ',
  },
  {
    id: 'wallpoet',
    name: 'Wallpoet',
    googleFamily: 'Wallpoet',
    googleFontQuery: 'Wallpoet',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Wallpoet', cursive, sans-serif",
    baseSize: 76,
    baseLineHeight: 108,
    sampleText: 'GRABADO EN EL CORAZÓN',
  },
  {
    id: 'share-tech-mono',
    name: 'Share Tech Mono',
    googleFamily: 'Share Tech Mono',
    googleFontQuery: 'Share+Tech+Mono',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Share Tech Mono', monospace",
    baseSize: 72,
    baseLineHeight: 104,
    sampleText: 'Coordenadas de nuestro amor',
  },
  {
    id: 'chakra-petch',
    name: 'Chakra Petch',
    googleFamily: 'Chakra Petch',
    googleFontQuery: 'Chakra+Petch:wght@400;600;700',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 700,
    cssFamily: "'Chakra Petch', sans-serif",
    baseSize: 74,
    baseLineHeight: 106,
    sampleText: 'Conexión que trasciende',
  },
  {
    id: 'goldman',
    name: 'Goldman',
    googleFamily: 'Goldman',
    googleFontQuery: 'Goldman:wght@400;700',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 700,
    cssFamily: "'Goldman', cursive, sans-serif",
    baseSize: 74,
    baseLineHeight: 106,
    sampleText: 'Para siempre a tu lado',
  },
  {
    id: 'major-mono',
    name: 'Major Mono Display',
    googleFamily: 'Major Mono Display',
    googleFontQuery: 'Major+Mono+Display',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Major Mono Display', monospace",
    baseSize: 70,
    baseLineHeight: 104,
    sampleText: 'tú y yo en perfecta calma',
  },
  {
    id: 'syne-mono',
    name: 'Syne Mono',
    googleFamily: 'Syne Mono',
    googleFontQuery: 'Syne+Mono',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Syne Mono', monospace",
    baseSize: 72,
    baseLineHeight: 104,
    sampleText: 'Un amor sin condiciones',
  },
  {
    id: 'russo-one',
    name: 'Russo One',
    googleFamily: 'Russo One',
    googleFontQuery: 'Russo+One',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Russo One', sans-serif",
    baseSize: 76,
    baseLineHeight: 110,
    sampleText: 'FUERTE COMO NUESTRO AMOR',
  },
  {
    id: 'staatliches',
    name: 'Staatliches',
    googleFamily: 'Staatliches',
    googleFontQuery: 'Staatliches',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Staatliches', cursive, sans-serif",
    baseSize: 82,
    baseLineHeight: 112,
    sampleText: 'NUESTRA PROMESA ETERNA',
  },
  {
    id: 'audiowide',
    name: 'Audiowide',
    googleFamily: 'Audiowide',
    googleFontQuery: 'Audiowide',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Audiowide', cursive, sans-serif",
    baseSize: 72,
    baseLineHeight: 106,
    sampleText: 'Sintonía de dos almas',
  },
  {
    id: 'michroma',
    name: 'Michroma',
    googleFamily: 'Michroma',
    googleFontQuery: 'Michroma',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Michroma', sans-serif",
    baseSize: 68,
    baseLineHeight: 102,
    sampleText: 'Amor en órbita perpetua',
  },
  {
    id: 'nova-square',
    name: 'Nova Square',
    googleFamily: 'Nova Square',
    googleFontQuery: 'Nova+Square',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Nova Square', cursive, sans-serif",
    baseSize: 74,
    baseLineHeight: 106,
    sampleText: 'Estructura inquebrantable',
  },
  {
    id: 'teko',
    name: 'Teko',
    googleFamily: 'Teko',
    googleFontQuery: 'Teko:wght@400;600;700',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 600,
    cssFamily: "'Teko', sans-serif",
    baseSize: 88,
    baseLineHeight: 114,
    sampleText: 'HASTA EL FIN DEL MUNDO',
  },
  {
    id: 'saira-stencil',
    name: 'Saira Stencil One',
    googleFamily: 'Saira Stencil One',
    googleFontQuery: 'Saira+Stencil+One',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Saira Stencil One', cursive, sans-serif",
    baseSize: 76,
    baseLineHeight: 108,
    sampleText: 'AMOR INQUEBRANTABLE',
  },
  {
    id: 'bungee',
    name: 'Bungee',
    googleFamily: 'Bungee',
    googleFontQuery: 'Bungee',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Bungee', cursive, sans-serif",
    baseSize: 68,
    baseLineHeight: 102,
    sampleText: 'AMOR AL MÁXIMO',
  },
  {
    id: 'oxanium',
    name: 'Oxanium',
    googleFamily: 'Oxanium',
    googleFontQuery: 'Oxanium:wght@400;600;700',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 600,
    cssFamily: "'Oxanium', cursive, sans-serif",
    baseSize: 74,
    baseLineHeight: 106,
    sampleText: 'Sincronía geométrica',
  },
  {
    id: 'righteous',
    name: 'Righteous',
    googleFamily: 'Righteous',
    googleFontQuery: 'Righteous',
    category: 'cuadrada',
    categoryLabel: 'Cuadrada',
    style: 'normal',
    weight: 400,
    cssFamily: "'Righteous', cursive, sans-serif",
    baseSize: 74,
    baseLineHeight: 106,
    sampleText: 'Magia que no se apaga',
  },

  // ========================================================
  // 3. MANUSCRITA / HANDWRITING (Diario íntimo y notas a mano)
  // ========================================================
  {
    id: 'caveat',
    name: 'Caveat',
    googleFamily: 'Caveat',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 700,
    cssFamily: "'Caveat', cursive, sans-serif",
    baseSize: 96,
    baseLineHeight: 126,
    sampleText: 'Mi amor por ti es eterno',
  },
  {
    id: 'kalam',
    name: 'Kalam',
    googleFamily: 'Kalam',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 700,
    cssFamily: "'Kalam', cursive",
    baseSize: 84,
    baseLineHeight: 118,
    sampleText: 'Nuestra historia de amor',
  },
  {
    id: 'indie-flower',
    name: 'Indie Flower',
    googleFamily: 'Indie Flower',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Indie Flower', cursive",
    baseSize: 82,
    baseLineHeight: 116,
    sampleText: 'Flores en tu mirada',
  },
  {
    id: 'patrick-hand',
    name: 'Patrick Hand',
    googleFamily: 'Patrick Hand',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Patrick Hand', cursive",
    baseSize: 84,
    baseLineHeight: 118,
    sampleText: 'Contigo cada segundo',
  },
  {
    id: 'shadows-into-light',
    name: 'Shadows Into Light',
    googleFamily: 'Shadows Into Light',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Shadows Into Light', cursive",
    baseSize: 84,
    baseLineHeight: 118,
    sampleText: 'Luz en la oscuridad',
  },
  {
    id: 'amatic-sc',
    name: 'Amatic SC',
    googleFamily: 'Amatic SC',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 700,
    cssFamily: "'Amatic SC', cursive",
    baseSize: 92,
    baseLineHeight: 110,
    sampleText: 'TE ELIJO SIEMPRE',
  },
  {
    id: 'gloria-hallelujah',
    name: 'Gloria Hallelujah',
    googleFamily: 'Gloria Hallelujah',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Gloria Hallelujah', cursive",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Alegría en el corazón',
  },
  {
    id: 'just-another-hand',
    name: 'Just Another Hand',
    googleFamily: 'Just Another Hand',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Just Another Hand', cursive",
    baseSize: 98,
    baseLineHeight: 120,
    sampleText: 'Escrito con el alma',
  },
  {
    id: 'reenie-beanie',
    name: 'Reenie Beanie',
    googleFamily: 'Reenie Beanie',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Reenie Beanie', cursive",
    baseSize: 96,
    baseLineHeight: 122,
    sampleText: 'Un suspiro para ti',
  },
  {
    id: 'gochi-hand',
    name: 'Gochi Hand',
    googleFamily: 'Gochi Hand',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Gochi Hand', cursive",
    baseSize: 84,
    baseLineHeight: 116,
    sampleText: 'Dulce complicidad',
  },
  {
    id: 'covered-by-your-grace',
    name: 'Covered By Your Grace',
    googleFamily: 'Covered By Your Grace',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Covered By Your Grace', cursive",
    baseSize: 86,
    baseLineHeight: 118,
    sampleText: 'Bendecidos de amor',
  },
  {
    id: 'nothing-you-could-do',
    name: 'Nothing You Could Do',
    googleFamily: 'Nothing You Could Do',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Nothing You Could Do', cursive",
    baseSize: 84,
    baseLineHeight: 116,
    sampleText: 'Sin condiciones',
  },
  {
    id: 'dekko',
    name: 'Dekko',
    googleFamily: 'Dekko',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Dekko', cursive",
    baseSize: 80,
    baseLineHeight: 112,
    sampleText: 'Páginas compartidas',
  },
  {
    id: 'delius',
    name: 'Delius',
    googleFamily: 'Delius',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Delius', cursive",
    baseSize: 78,
    baseLineHeight: 112,
    sampleText: 'Serenidad en tu voz',
  },
  {
    id: 'nanum-pen-script',
    name: 'Nanum Pen',
    googleFamily: 'Nanum Pen Script',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Nanum Pen Script', cursive",
    baseSize: 90,
    baseLineHeight: 120,
    sampleText: 'Tinta indeleble',
  },
  {
    id: 'sedgwick-ave',
    name: 'Sedgwick Ave',
    googleFamily: 'Sedgwick Ave',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Sedgwick Ave', cursive",
    baseSize: 80,
    baseLineHeight: 114,
    sampleText: 'Pasión sin límites',
  },
  {
    id: 'crafty-girls',
    name: 'Crafty Girls',
    googleFamily: 'Crafty Girls',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Crafty Girls', cursive",
    baseSize: 78,
    baseLineHeight: 112,
    sampleText: 'Detalles que enamoran',
  },
  {
    id: 'schoolbell',
    name: 'Schoolbell',
    googleFamily: 'Schoolbell',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Schoolbell', cursive",
    baseSize: 82,
    baseLineHeight: 114,
    sampleText: 'Primer amor',
  },
  {
    id: 'bad-script',
    name: 'Bad Script',
    googleFamily: 'Bad Script',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Bad Script', cursive",
    baseSize: 84,
    baseLineHeight: 116,
    sampleText: 'Trazos sinceros',
  },
  {
    id: 'marck-script',
    name: 'Marck Script',
    googleFamily: 'Marck Script',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Marck Script', cursive",
    baseSize: 86,
    baseLineHeight: 118,
    sampleText: 'Carta de amor',
  },
  {
    id: 'pangolin',
    name: 'Pangolin',
    googleFamily: 'Pangolin',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Pangolin', cursive",
    baseSize: 78,
    baseLineHeight: 112,
    sampleText: 'Sonrisa espontánea',
  },
  {
    id: 'walter-turncoat',
    name: 'Walter Turncoat',
    googleFamily: 'Walter Turncoat',
    category: 'manuscrita',
    categoryLabel: 'Manuscrita',
    style: 'normal',
    weight: 400,
    cssFamily: "'Walter Turncoat', cursive",
    baseSize: 78,
    baseLineHeight: 114,
    sampleText: 'Aventura a tu lado',
  },

  // ========================================================
  // 2. CURSIVA / CALIGRAFÍA ROMÁNTICA (Elegancia fluida)
  // ========================================================
  {
    id: 'dancing',
    name: 'Dancing Script',
    googleFamily: 'Dancing Script',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 700,
    cssFamily: "'Dancing Script', cursive, serif",
    baseSize: 86,
    baseLineHeight: 120,
    sampleText: 'Bailando en tus ojos',
  },
  {
    id: 'great-vibes',
    name: 'Great Vibes',
    googleFamily: 'Great Vibes',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Great Vibes', cursive",
    baseSize: 92,
    baseLineHeight: 126,
    sampleText: 'Amor de novela',
  },
  {
    id: 'alex-brush',
    name: 'Alex Brush',
    googleFamily: 'Alex Brush',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Alex Brush', cursive",
    baseSize: 94,
    baseLineHeight: 126,
    sampleText: 'Pinceladas de pasión',
  },
  {
    id: 'sacramento',
    name: 'Sacramento',
    googleFamily: 'Sacramento',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Sacramento', cursive",
    baseSize: 94,
    baseLineHeight: 122,
    sampleText: 'Promesa eterna',
  },
  {
    id: 'parisienne',
    name: 'Parisienne',
    googleFamily: 'Parisienne',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Parisienne', cursive",
    baseSize: 90,
    baseLineHeight: 122,
    sampleText: 'Romance en París',
  },
  {
    id: 'allura',
    name: 'Allura',
    googleFamily: 'Allura',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Allura', cursive",
    baseSize: 96,
    baseLineHeight: 128,
    sampleText: 'Devoción infinita',
  },
  {
    id: 'tangerine',
    name: 'Tangerine',
    googleFamily: 'Tangerine',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 700,
    cssFamily: "'Tangerine', cursive",
    baseSize: 110,
    baseLineHeight: 130,
    sampleText: 'Sutileza dorada',
  },
  {
    id: 'pinyon-script',
    name: 'Pinyon Script',
    googleFamily: 'Pinyon Script',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Pinyon Script', cursive",
    baseSize: 94,
    baseLineHeight: 126,
    sampleText: 'Aristocracia y amor',
  },
  {
    id: 'italianno',
    name: 'Italianno',
    googleFamily: 'Italianno',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Italianno', cursive",
    baseSize: 104,
    baseLineHeight: 126,
    sampleText: 'Passione pura',
  },
  {
    id: 'rochester',
    name: 'Rochester',
    googleFamily: 'Rochester',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Rochester', cursive",
    baseSize: 84,
    baseLineHeight: 118,
    sampleText: 'Tiempo detenido',
  },
  {
    id: 'rouge-script',
    name: 'Rouge Script',
    googleFamily: 'Rouge Script',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Rouge Script', cursive",
    baseSize: 96,
    baseLineHeight: 124,
    sampleText: 'Beso carmesí',
  },
  {
    id: 'arizonia',
    name: 'Arizonia',
    googleFamily: 'Arizonia',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Arizonia', cursive",
    baseSize: 92,
    baseLineHeight: 124,
    sampleText: 'Calidez en el desierto',
  },
  {
    id: 'montez',
    name: 'Montez',
    googleFamily: 'Montez',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Montez', cursive",
    baseSize: 86,
    baseLineHeight: 120,
    sampleText: 'Dulzura que abraza',
  },
  {
    id: 'yellowtail',
    name: 'Yellowtail',
    googleFamily: 'Yellowtail',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Yellowtail', cursive",
    baseSize: 80,
    baseLineHeight: 116,
    sampleText: 'Ritmo del corazón',
  },
  {
    id: 'stalemate',
    name: 'Stalemate',
    googleFamily: 'Stalemate',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Stalemate', cursive",
    baseSize: 104,
    baseLineHeight: 130,
    sampleText: 'Perderme en ti',
  },
  {
    id: 'bilbo-swash-caps',
    name: 'Bilbo Swash',
    googleFamily: 'Bilbo Swash Caps',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Bilbo Swash Caps', cursive",
    baseSize: 88,
    baseLineHeight: 120,
    sampleText: 'Encanto especial',
  },
  {
    id: 'lovers-quarrel',
    name: 'Lovers Quarrel',
    googleFamily: 'Lovers Quarrel',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Lovers Quarrel', cursive",
    baseSize: 106,
    baseLineHeight: 132,
    sampleText: 'Reconciliación y dicha',
  },
  {
    id: 'clicker-script',
    name: 'Clicker Script',
    googleFamily: 'Clicker Script',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Clicker Script', cursive",
    baseSize: 90,
    baseLineHeight: 122,
    sampleText: 'Chispa de amor',
  },
  {
    id: 'ruthie',
    name: 'Ruthie',
    googleFamily: 'Ruthie',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Ruthie', cursive",
    baseSize: 110,
    baseLineHeight: 134,
    sampleText: 'Líneas de ternura',
  },
  {
    id: 'qwigley',
    name: 'Qwigley',
    googleFamily: 'Qwigley',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Qwigley', cursive",
    baseSize: 106,
    baseLineHeight: 132,
    sampleText: 'Alas de libertad',
  },
  {
    id: 'meddon',
    name: 'Meddon',
    googleFamily: 'Meddon',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Meddon', cursive",
    baseSize: 84,
    baseLineHeight: 120,
    sampleText: 'Manuscrito antiguo',
  },
  {
    id: 'meie-script',
    name: 'Meie Script',
    googleFamily: 'Meie Script',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Meie Script', cursive",
    baseSize: 90,
    baseLineHeight: 124,
    sampleText: 'Suave como la brisa',
  },
  {
    id: 'dawning-of-a-new-day',
    name: 'Dawning Day',
    googleFamily: 'Dawning of a New Day',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Dawning of a New Day', cursive",
    baseSize: 90,
    baseLineHeight: 124,
    sampleText: 'Un nuevo amanecer',
  },
  {
    id: 'petit-formal-script',
    name: 'Petit Formal',
    googleFamily: 'Petit Formal Script',
    category: 'cursiva',
    categoryLabel: 'Cursiva',
    style: 'normal',
    weight: 400,
    cssFamily: "'Petit Formal Script', cursive",
    baseSize: 86,
    baseLineHeight: 122,
    sampleText: 'Elegancia íntima',
  },

  // ========================================================
  // 3. ELEGANTE / SERIF DE ÉPOCA (Novelas clásicas y distinciones)
  // ========================================================
  {
    id: 'playfair',
    name: 'Playfair Display',
    googleFamily: 'Playfair Display',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 600,
    cssFamily: "'Playfair Display', Georgia, serif",
    baseSize: 76,
    baseLineHeight: 114,
    sampleText: 'Amor de colección',
  },
  {
    id: 'cinzel',
    name: 'Cinzel',
    googleFamily: 'Cinzel',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 600,
    cssFamily: "'Cinzel', serif",
    baseSize: 72,
    baseLineHeight: 112,
    sampleText: 'IMPERIO DE NUESTRO AMOR',
  },
  {
    id: 'prata',
    name: 'Prata',
    googleFamily: 'Prata',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 400,
    cssFamily: "'Prata', serif",
    baseSize: 74,
    baseLineHeight: 114,
    sampleText: 'Belleza inmarcesible',
  },
  {
    id: 'bodoni-moda',
    name: 'Bodoni Moda',
    googleFamily: 'Bodoni Moda',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 600,
    cssFamily: "'Bodoni Moda', serif",
    baseSize: 76,
    baseLineHeight: 116,
    sampleText: 'Alta costura en versos',
  },
  {
    id: 'marcellus',
    name: 'Marcellus',
    googleFamily: 'Marcellus',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 400,
    cssFamily: "'Marcellus', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Serenidad esculpida',
  },
  {
    id: 'bellefair',
    name: 'Bellefair',
    googleFamily: 'Bellefair',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 400,
    cssFamily: "'Bellefair', serif",
    baseSize: 76,
    baseLineHeight: 114,
    sampleText: 'Sutileza y devoción',
  },
  {
    id: 'castoro',
    name: 'Castoro',
    googleFamily: 'Castoro',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 400,
    cssFamily: "'Castoro', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Raíces profundas',
  },
  {
    id: 'vidaloka',
    name: 'Vidaloka',
    googleFamily: 'Vidaloka',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 400,
    cssFamily: "'Vidaloka', serif",
    baseSize: 74,
    baseLineHeight: 114,
    sampleText: 'Fuerza y romance',
  },
  {
    id: 'faustina',
    name: 'Faustina',
    googleFamily: 'Faustina',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 600,
    cssFamily: "'Faustina', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Lectura reposada',
  },
  {
    id: 'libre-baskerville',
    name: 'Libre Baskerville',
    googleFamily: 'Libre Baskerville',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 400,
    cssFamily: "'Libre Baskerville', serif",
    baseSize: 70,
    baseLineHeight: 110,
    sampleText: 'Clásico imperecedero',
  },
  {
    id: 'eb-garamond',
    name: 'EB Garamond',
    googleFamily: 'EB Garamond',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 500,
    cssFamily: "'EB Garamond', serif",
    baseSize: 76,
    baseLineHeight: 114,
    sampleText: 'Siglos de literatura',
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    googleFamily: 'Merriweather',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 400,
    cssFamily: "'Merriweather', serif",
    baseSize: 68,
    baseLineHeight: 108,
    sampleText: 'Calidez en cada letra',
  },
  {
    id: 'lora',
    name: 'Lora',
    googleFamily: 'Lora',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 500,
    cssFamily: "'Lora', serif",
    baseSize: 72,
    baseLineHeight: 112,
    sampleText: 'Melodía suave',
  },
  {
    id: 'spectral',
    name: 'Spectral',
    googleFamily: 'Spectral',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 500,
    cssFamily: "'Spectral', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Luz tenue del alma',
  },
  {
    id: 'cardo',
    name: 'Cardo',
    googleFamily: 'Cardo',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 400,
    cssFamily: "'Cardo', serif",
    baseSize: 74,
    baseLineHeight: 114,
    sampleText: 'Pergamino sagrado',
  },
  {
    id: 'frank-ruhl-libre',
    name: 'Frank Ruhl Libre',
    googleFamily: 'Frank Ruhl Libre',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 500,
    cssFamily: "'Frank Ruhl Libre', serif",
    baseSize: 72,
    baseLineHeight: 110,
    sampleText: 'Nobleza en la palabra',
  },
  {
    id: 'alice',
    name: 'Alice',
    googleFamily: 'Alice',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'normal',
    weight: 400,
    cssFamily: "'Alice', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Mundo de fantasía',
  },
  {
    id: 'sorts-mill-goudy',
    name: 'Sorts Mill Goudy',
    googleFamily: 'Sorts Mill Goudy',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 400,
    cssFamily: "'Sorts Mill Goudy', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Orígenes poéticos',
  },
  {
    id: 'vollkorn',
    name: 'Vollkorn',
    googleFamily: 'Vollkorn',
    category: 'elegante',
    categoryLabel: 'Elegante',
    style: 'italic',
    weight: 500,
    cssFamily: "'Vollkorn', serif",
    baseSize: 72,
    baseLineHeight: 110,
    sampleText: 'Fortaleza del amor',
  },

  // ========================================================
  // 4. POÉTICA / EDITORIAL & LÍRICA (Mística, antologías y versos)
  // ========================================================
  {
    id: 'cormorant',
    name: 'Cormorant Garamond',
    googleFamily: 'Cormorant Garamond',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'italic',
    weight: 600,
    cssFamily: "'Cormorant Garamond', Georgia, serif",
    baseSize: 88,
    baseLineHeight: 118,
    sampleText: 'Versos que acarician',
  },
  {
    id: 'cormorant-infant',
    name: 'Cormorant Infant',
    googleFamily: 'Cormorant Infant',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'italic',
    weight: 600,
    cssFamily: "'Cormorant Infant', serif",
    baseSize: 86,
    baseLineHeight: 116,
    sampleText: 'Inocencia y ternura',
  },
  {
    id: 'cormorant-upright',
    name: 'Cormorant Upright',
    googleFamily: 'Cormorant Upright',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 600,
    cssFamily: "'Cormorant Upright', serif",
    baseSize: 84,
    baseLineHeight: 116,
    sampleText: 'Dignidad poética',
  },
  {
    id: 'elsie',
    name: 'Elsie',
    googleFamily: 'Elsie',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Elsie', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Canto de sirena',
  },
  {
    id: 'yeseva-one',
    name: 'Yeseva One',
    googleFamily: 'Yeseva One',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Yeseva One', serif",
    baseSize: 70,
    baseLineHeight: 110,
    sampleText: 'Firmeza femenina',
  },
  {
    id: 'dm-serif-display',
    name: 'DM Serif Display',
    googleFamily: 'DM Serif Display',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'italic',
    weight: 400,
    cssFamily: "'DM Serif Display', serif",
    baseSize: 76,
    baseLineHeight: 114,
    sampleText: 'Declaración pública',
  },
  {
    id: 'rozha-one',
    name: 'Rozha One',
    googleFamily: 'Rozha One',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Rozha One', serif",
    baseSize: 74,
    baseLineHeight: 114,
    sampleText: 'Contraste sublime',
  },
  {
    id: 'abril-fatface',
    name: 'Abril Fatface',
    googleFamily: 'Abril Fatface',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Abril Fatface', cursive",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Amor en primavera',
  },
  {
    id: 'noto-serif',
    name: 'Noto Serif',
    googleFamily: 'Noto Serif',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'italic',
    weight: 400,
    cssFamily: "'Noto Serif', serif",
    baseSize: 70,
    baseLineHeight: 110,
    sampleText: 'Armonía universal',
  },
  {
    id: 'halant',
    name: 'Halant',
    googleFamily: 'Halant',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 500,
    cssFamily: "'Halant', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Paz en el espíritu',
  },
  {
    id: 'domine',
    name: 'Domine',
    googleFamily: 'Domine',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 500,
    cssFamily: "'Domine', serif",
    baseSize: 72,
    baseLineHeight: 110,
    sampleText: 'Lectura del corazón',
  },
  {
    id: 'oranienbaum',
    name: 'Oranienbaum',
    googleFamily: 'Oranienbaum',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Oranienbaum', serif",
    baseSize: 74,
    baseLineHeight: 114,
    sampleText: 'Palacio de ensueño',
  },
  {
    id: 'suranna',
    name: 'Suranna',
    googleFamily: 'Suranna',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Suranna', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Lirismo íntimo',
  },
  {
    id: 'arapey',
    name: 'Arapey',
    googleFamily: 'Arapey',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'italic',
    weight: 400,
    cssFamily: "'Arapey', serif",
    baseSize: 78,
    baseLineHeight: 114,
    sampleText: 'Vuelo de golondrina',
  },
  {
    id: 'tenor-sans',
    name: 'Tenor Sans',
    googleFamily: 'Tenor Sans',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'normal',
    weight: 400,
    cssFamily: "'Tenor Sans', sans-serif",
    baseSize: 72,
    baseLineHeight: 110,
    sampleText: 'Voz lírica y clara',
  },
  {
    id: 'fraunces',
    name: 'Fraunces',
    googleFamily: 'Fraunces',
    category: 'poetica',
    categoryLabel: 'Poética',
    style: 'italic',
    weight: 600,
    cssFamily: "'Fraunces', serif",
    baseSize: 74,
    baseLineHeight: 112,
    sampleText: 'Calidez artesanal',
  },

  // ========================================================
  // 5. MODERNA / SANS-SERIF LIMPIA (Vanguardista y fresca)
  // ========================================================
  {
    id: 'jakarta',
    name: 'Plus Jakarta Sans',
    googleFamily: 'Plus Jakarta Sans',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Plus Jakarta Sans', sans-serif",
    baseSize: 70,
    baseLineHeight: 104,
    sampleText: 'Claridad en cada día',
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    googleFamily: 'Montserrat',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Montserrat', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Horizontes compartidos',
  },
  {
    id: 'poppins',
    name: 'Poppins',
    googleFamily: 'Poppins',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Poppins', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Geometría del afecto',
  },
  {
    id: 'inter',
    name: 'Inter',
    googleFamily: 'Inter',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Inter', sans-serif",
    baseSize: 66,
    baseLineHeight: 102,
    sampleText: 'Perfección sincera',
  },
  {
    id: 'raleway',
    name: 'Raleway',
    googleFamily: 'Raleway',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Raleway', sans-serif",
    baseSize: 70,
    baseLineHeight: 106,
    sampleText: 'Elegancia contemporánea',
  },
  {
    id: 'quicksand',
    name: 'Quicksand',
    googleFamily: 'Quicksand',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Quicksand', sans-serif",
    baseSize: 72,
    baseLineHeight: 108,
    sampleText: 'Bordes suaves y tiernos',
  },
  {
    id: 'outfit',
    name: 'Outfit',
    googleFamily: 'Outfit',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Outfit', sans-serif",
    baseSize: 70,
    baseLineHeight: 106,
    sampleText: 'Energía positiva',
  },
  {
    id: 'urbanist',
    name: 'Urbanist',
    googleFamily: 'Urbanist',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Urbanist', sans-serif",
    baseSize: 70,
    baseLineHeight: 106,
    sampleText: 'Pasos en la ciudad',
  },
  {
    id: 'syne',
    name: 'Syne',
    googleFamily: 'Syne',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Syne', sans-serif",
    baseSize: 70,
    baseLineHeight: 106,
    sampleText: 'Personalidad única',
  },
  {
    id: 'space-grotesk',
    name: 'Space Grotesk',
    googleFamily: 'Space Grotesk',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Space Grotesk', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Viaje a las estrellas',
  },
  {
    id: 'manrope',
    name: 'Manrope',
    googleFamily: 'Manrope',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Manrope', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Equilibrio perfecto',
  },
  {
    id: 'nunito',
    name: 'Nunito',
    googleFamily: 'Nunito',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Nunito', sans-serif",
    baseSize: 70,
    baseLineHeight: 106,
    sampleText: 'Ternura redondeada',
  },
  {
    id: 'work-sans',
    name: 'Work Sans',
    googleFamily: 'Work Sans',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Work Sans', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Construir el futuro',
  },
  {
    id: 'dm-sans',
    name: 'DM Sans',
    googleFamily: 'DM Sans',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'DM Sans', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Diálogo constante',
  },
  {
    id: 'rubik',
    name: 'Rubik',
    googleFamily: 'Rubik',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Rubik', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Juego y risas',
  },
  {
    id: 'comfortaa',
    name: 'Comfortaa',
    googleFamily: 'Comfortaa',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Comfortaa', cursive",
    baseSize: 72,
    baseLineHeight: 108,
    sampleText: 'Cálido abrazo',
  },
  {
    id: 'josefin-sans',
    name: 'Josefin Sans',
    googleFamily: 'Josefin Sans',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 600,
    cssFamily: "'Josefin Sans', sans-serif",
    baseSize: 72,
    baseLineHeight: 108,
    sampleText: 'Estilo escandinavo',
  },
  {
    id: 'lexend',
    name: 'Lexend',
    googleFamily: 'Lexend',
    category: 'moderna',
    categoryLabel: 'Moderna',
    style: 'normal',
    weight: 500,
    cssFamily: "'Lexend', sans-serif",
    baseSize: 68,
    baseLineHeight: 104,
    sampleText: 'Lectura sin esfuerzo',
  },

  // ========================================================
  // 6. VINTAGE / RETRO & MONOSPACE (Máquina de escribir y nostalgia)
  // ========================================================
  {
    id: 'courier',
    name: 'Courier Prime',
    googleFamily: 'Courier Prime',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 700,
    cssFamily: "'Courier Prime', Courier, monospace",
    baseSize: 66,
    baseLineHeight: 98,
    sampleText: 'Teletipo del corazón',
  },
  {
    id: 'special-elite',
    name: 'Special Elite',
    googleFamily: 'Special Elite',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 400,
    cssFamily: "'Special Elite', cursive",
    baseSize: 70,
    baseLineHeight: 102,
    sampleText: 'Máquina Remington 1940',
  },
  {
    id: 'cutive-mono',
    name: 'Cutive Mono',
    googleFamily: 'Cutive Mono',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 400,
    cssFamily: "'Cutive Mono', monospace",
    baseSize: 68,
    baseLineHeight: 100,
    sampleText: 'Página mecanografiada',
  },
  {
    id: 'space-mono',
    name: 'Space Mono',
    googleFamily: 'Space Mono',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'italic',
    weight: 700,
    cssFamily: "'Space Mono', monospace",
    baseSize: 64,
    baseLineHeight: 98,
    sampleText: 'Misión: Tu Felicidad',
  },
  {
    id: 'ibm-plex-mono',
    name: 'IBM Plex Mono',
    googleFamily: 'IBM Plex Mono',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'italic',
    weight: 500,
    cssFamily: "'IBM Plex Mono', monospace",
    baseSize: 64,
    baseLineHeight: 98,
    sampleText: 'Código de amor',
  },
  {
    id: 'anonymous-pro',
    name: 'Anonymous Pro',
    googleFamily: 'Anonymous Pro',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 700,
    cssFamily: "'Anonymous Pro', monospace",
    baseSize: 66,
    baseLineHeight: 98,
    sampleText: 'Cartas secretas',
  },
  {
    id: 'rye',
    name: 'Rye',
    googleFamily: 'Rye',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 400,
    cssFamily: "'Rye', cursive",
    baseSize: 68,
    baseLineHeight: 102,
    sampleText: 'Leyenda del Oeste',
  },
  {
    id: 'fredericka-the-great',
    name: 'Fredericka The Great',
    googleFamily: 'Fredericka the Great',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 400,
    cssFamily: "'Fredericka the Great', cursive",
    baseSize: 76,
    baseLineHeight: 110,
    sampleText: 'Grabado a mano',
  },
  {
    id: 'ribeye',
    name: 'Ribeye',
    googleFamily: 'Ribeye',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 400,
    cssFamily: "'Ribeye', cursive",
    baseSize: 72,
    baseLineHeight: 108,
    sampleText: 'Tatuaje romántico',
  },
  {
    id: 'monofett',
    name: 'Monofett',
    googleFamily: 'Monofett',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'normal',
    weight: 400,
    cssFamily: "'Monofett', monospace",
    baseSize: 70,
    baseLineHeight: 100,
    sampleText: 'RETRO FUTURE',
  },
  {
    id: 'fondamento',
    name: 'Fondamento',
    googleFamily: 'Fondamento',
    category: 'vintage',
    categoryLabel: 'Vintage',
    style: 'italic',
    weight: 400,
    cssFamily: "'Fondamento', cursive",
    baseSize: 78,
    baseLineHeight: 114,
    sampleText: 'Códice renacentista',
  },

  // ========================================================
  // 7. DECORATIVA & EXPRESIVA (Fantasía, cartelería y encanto)
  // ========================================================
  {
    id: 'cinzel-decorative',
    name: 'Cinzel Decorative',
    googleFamily: 'Cinzel Decorative',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 700,
    cssFamily: "'Cinzel Decorative', serif",
    baseSize: 74,
    baseLineHeight: 114,
    sampleText: 'LAUREL DE AMOR',
  },
  {
    id: 'poiret-one',
    name: 'Poiret One',
    googleFamily: 'Poiret One',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Poiret One', cursive",
    baseSize: 76,
    baseLineHeight: 112,
    sampleText: 'Art Déco y Glamour',
  },
  {
    id: 'pacifico',
    name: 'Pacifico',
    googleFamily: 'Pacifico',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Pacifico', cursive",
    baseSize: 78,
    baseLineHeight: 114,
    sampleText: 'Olas de dulzura',
  },
  {
    id: 'lobster',
    name: 'Lobster',
    googleFamily: 'Lobster',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Lobster', cursive",
    baseSize: 78,
    baseLineHeight: 114,
    sampleText: 'Siempre juntos',
  },
  {
    id: 'lobster-two',
    name: 'Lobster Two',
    googleFamily: 'Lobster Two',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'italic',
    weight: 700,
    cssFamily: "'Lobster Two', cursive",
    baseSize: 80,
    baseLineHeight: 116,
    sampleText: 'Alegres recuerdos',
  },
  {
    id: 'satisfy',
    name: 'Satisfy',
    googleFamily: 'Satisfy',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Satisfy', cursive",
    baseSize: 82,
    baseLineHeight: 118,
    sampleText: 'Plenitud total',
  },
  {
    id: 'kaushan-script',
    name: 'Kaushan Script',
    googleFamily: 'Kaushan Script',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Kaushan Script', cursive",
    baseSize: 80,
    baseLineHeight: 116,
    sampleText: 'Fuerza expresiva',
  },
  {
    id: 'berkshire-swash',
    name: 'Berkshire Swash',
    googleFamily: 'Berkshire Swash',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Berkshire Swash', cursive",
    baseSize: 76,
    baseLineHeight: 112,
    sampleText: 'Cuento de hadas',
  },
  {
    id: 'homemade-apple',
    name: 'Homemade Apple',
    googleFamily: 'Homemade Apple',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Homemade Apple', cursive",
    baseSize: 86,
    baseLineHeight: 122,
    sampleText: 'Receta de felicidad',
  },
  {
    id: 'monoton',
    name: 'Monoton',
    googleFamily: 'Monoton',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Monoton', cursive",
    baseSize: 70,
    baseLineHeight: 104,
    sampleText: 'AMOR NEÓN',
  },
  {
    id: 'shrikhand',
    name: 'Shrikhand',
    googleFamily: 'Shrikhand',
    category: 'decorativa',
    categoryLabel: 'Decorativa',
    style: 'normal',
    weight: 400,
    cssFamily: "'Shrikhand', cursive",
    baseSize: 74,
    baseLineHeight: 110,
    sampleText: 'Pasión audaz',
  },
];

// Mapa indexado para acceso O(1)
export const FONTS_BY_ID: Record<string, RomanticFontItem> = ALL_ROMANTIC_FONTS.reduce(
  (acc, font) => {
    acc[font.id] = font;
    return acc;
  },
  {} as Record<string, RomanticFontItem>
);

// Cache de fuentes cargadas en el DOM
const loadedFontsSet = new Set<string>([
  'caveat',
  'dancing',
  'playfair',
  'cormorant',
  'courier',
  'jakarta',
  'press-start',
  'silkscreen',
  'pixelify',
  'vt323',
  'orbitron',
  'squada-one',
]);

/**
 * Carga dinámicamente una Google Font en el head del navegador si aún no está presente
 */
export function ensureGoogleFontLoaded(fontId: string): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(true);

  const font = FONTS_BY_ID[fontId];
  if (!font) return Promise.resolve(false);

  if (loadedFontsSet.has(font.id)) {
    return Promise.resolve(true);
  }

  const linkId = `gfont-dyn-${font.id}`;
  if (document.getElementById(linkId)) {
    loadedFontsSet.add(font.id);
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';

    // Generar URL optimizada de Google Fonts
    const familyParam = font.googleFontQuery
      ? font.googleFontQuery
      : encodeURIComponent(font.googleFamily).replace(/%20/g, '+');

    link.href = `https://fonts.googleapis.com/css2?family=${familyParam}&display=swap`;

    link.onload = () => {
      loadedFontsSet.add(font.id);
      // Forzar chequeo en document.fonts si está disponible
      if ('fonts' in document) {
        document.fonts.ready.then(() => resolve(true)).catch(() => resolve(true));
      } else {
        resolve(true);
      }
    };

    link.onerror = () => {
      console.warn(`No se pudo cargar la fuente remota ${font.googleFamily}, se usará fallback.`);
      resolve(false);
    };

    document.head.appendChild(link);
  });
}
